import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Alert, Button, FormHelperText, IconButton, MenuItem, Select, TextField } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDialog } from "hooks/useDialog.ts";
import { getFormFieldProps } from "utils/form";
import { useDeclineCustomQuoteMutation } from "features/api/customQuotesApi.ts";
import {
	DeclineReasonLabel,
	DeclineReasonSectionDescription,
	DeclineReasonSectionTitle,
	DeclineRequestButtonText,
	DeclineRequestModalTitle,
	DeclineRequestRequiredFieldText,
	FailedToDeclineRequestText,
	MessageDesignerPlaceholder,
	MessageDesignerSectionDescription,
	MessageDesignerSectionTitle,
	ModalCancel
} from "constants/text.ts";
import { DECLINE_REASONS, DeclineRequestFormData, declineRequestFormSchema } from "types/forms/declineRequestForm.ts";
import {
	DeclineReasonDescription,
	DeclineReasonMenuItem,
	DeclineReasonTitle,
	MessageContainer,
	PlaceholderText,
	RequiredFieldNotice,
	SectionContainer,
	SectionDescription,
	SectionTitle,
	StyledDialog,
	StyledDialogActions,
	StyledDialogContent,
	StyledDialogTitle,
	StyledFormControl
} from "./DeclineRequestModal.styles";
import { useNavigate } from "react-router";
import { useEffect } from "react";

interface DeclineRequestModalProps {
	quoteId: string;
	onConfirm?: (data: DeclineRequestFormData) => void;
}

const DeclineRequestModal = ({ quoteId, onConfirm }: DeclineRequestModalProps) => {
	const { closeDialog } = useDialog();
	const [declineCustomQuote, { error: declineError }] = useDeclineCustomQuoteMutation();
	const navigate = useNavigate();

	const {
		control,
		register,
		handleSubmit,
		watch,
		formState,
		setValue,
		formState: { errors }
	} = useForm<DeclineRequestFormData>({
		resolver: yupResolver(declineRequestFormSchema),
		mode: "onSubmit",
		reValidateMode: "onChange"
	});

	const messageValue = watch("message") || "";
	const formFieldData = { formState, register, schema: declineRequestFormSchema };

	const onSubmit = async (data: DeclineRequestFormData) => {
		try {
			await declineCustomQuote({
				quoteId,
				declineReasonCode: data.declineReason,
				comment: data.message
			}).unwrap();
			onConfirm?.(data);
			closeDialog();
		} catch (err) {
			// Error is handled by the error state from the mutation
			console.error("Failed to decline custom quote:", err);
		} finally {
			navigate("/custom-quote");
		}
	};

	const handleCancel = () => {
		closeDialog();
	};

	useEffect(() => {
		setValue("message", MessageDesignerPlaceholder);
	}, [setValue]);

	return (
		<StyledDialog
			open={true}
			onClose={handleCancel}
			maxWidth="md"
			fullWidth
			data-testid="decline-request-modal"
		>
			<StyledDialogTitle>
				{DeclineRequestModalTitle}
				<IconButton
					onClick={handleCancel}
					data-testid="close-modal-button"
				>
					<CloseIcon />
				</IconButton>
			</StyledDialogTitle>

			<StyledDialogContent>
				<RequiredFieldNotice>{DeclineRequestRequiredFieldText}</RequiredFieldNotice>

				{declineError && <Alert severity="error">{FailedToDeclineRequestText}</Alert>}

				<SectionContainer>
					<SectionTitle>{DeclineReasonSectionTitle}</SectionTitle>
					<SectionDescription>{DeclineReasonSectionDescription}</SectionDescription>

					<Controller
						name="declineReason"
						control={control}
						render={({ field }) => (
							<StyledFormControl
								fullWidth
								error={!!errors.declineReason}
								data-testid="decline-reason-select"
							>
								<Select
									{...field}
									displayEmpty
									sx={{
										height: "40px"
									}}
									MenuProps={{
										PaperProps: {
											style: {
												maxWidth: 500
											}
										}
									}}
									renderValue={(selected) => {
										if (!selected) {
											return <PlaceholderText>{DeclineReasonLabel}</PlaceholderText>;
										}
										const reason = DECLINE_REASONS.find((r) => r.value === selected);
										return reason?.label;
									}}
								>
									{DECLINE_REASONS.map((reason) => (
										<MenuItem
											key={reason.value}
											value={reason.value}
											data-testid={`decline-reason-option-${reason.value}`}
										>
											<DeclineReasonMenuItem>
												<DeclineReasonTitle>{reason.label}</DeclineReasonTitle>
												<DeclineReasonDescription>
													{reason.description}
												</DeclineReasonDescription>
											</DeclineReasonMenuItem>
										</MenuItem>
									))}
								</Select>
								{errors.declineReason && (
									<FormHelperText>{errors.declineReason.message}</FormHelperText>
								)}
							</StyledFormControl>
						)}
					/>
				</SectionContainer>

				<SectionContainer>
					<SectionTitle>{MessageDesignerSectionTitle}</SectionTitle>
					<SectionDescription>{MessageDesignerSectionDescription}</SectionDescription>

					<MessageContainer>
						<TextField
							{...getFormFieldProps({
								name: "message",
								characterLimit: {
									currentLength: messageValue.length,
									limit: 1000
								},
								...formFieldData
							})}
							multiline
							rows={8}
							fullWidth
							defaultValue={MessageDesignerPlaceholder}
							data-testid="message-designer-input"
						/>
					</MessageContainer>
				</SectionContainer>
			</StyledDialogContent>
			<StyledDialogActions>
				<Button
					onClick={handleCancel}
					variant="outlined"
					data-testid="decline-modal-cancel-button"
				>
					{ModalCancel}
				</Button>
				<Button
					onClick={handleSubmit(onSubmit)}
					variant="contained"
					color="primary"
					data-testid="decline-modal-confirm-button"
				>
					{DeclineRequestButtonText}
				</Button>
			</StyledDialogActions>
		</StyledDialog>
	);
};

export default DeclineRequestModal;
